import { useCallback } from 'react';
import {
  applyDurationResizeToEffect,
  calculateTimelineBounds,
  calculateTimelinePosition,
  findNextAvailableRow,
  getEffectSpeedKey,
  reorganizeEffectRows,
  snapTimeToMilli,
} from '../utils/timelineUtils';
import { ANIMATION_PROPERTY_CONFIGS } from '../AnimationComponent/constants/animationPropertyConfig';
import {
  objectHasGlbAnimation,
  resolveAnimationSceneObject,
} from '../AnimationComponent/handlers/animationEffectHandlers';

export const useTimelineHandlers = (state) => {
  const {
    timelineEffects,
    setTimelineEffects,
    selectedObject,
    setSelectedObject,
    selectedEffects,
    setSelectedEffects,
    isDraggingOverTimeline,
    setIsDraggingOverTimeline,
    draggedTimelineEffect,
    setDraggedTimelineEffect,
    dragOffset,
    setDragOffset,
    draggedEffect,
    setDraggedEffect,
    timelineScale,
    effectStartTime,
    setEffectStartTime,
    effectEndTime,
    setEffectEndTime,
    maxTimelineDuration,
    setMaxTimelineDuration,
    currentTimeIndicator,
    setCurrentTimeIndicator,
    isDraggingIndicator,
    setIsDraggingIndicator,
    animationSettings,
    setAnimationSettings,
    isPlaying,
    setIsPlaying,
    setPlaybackStartTime,
    timelineContainerRef,
    scene,
  } = state;

  const PIXELS_PER_MARKER = 100;
  const DEFAULT_NEW_EFFECT_DURATION = 3; // default duration for newly added effects

  const canAddPlayModelToObject = useCallback(
    (objectId) => {
      const sceneObj = resolveAnimationSceneObject(scene, objectId);
      return objectHasGlbAnimation(sceneObj);
    },
    [scene]
  );

  // Timeline drop handlers
  const handleTimelineDrop = useCallback(
    (e, objectId) => {
      e.preventDefault();
      setIsDraggingOverTimeline(false);

      try {
        const data = JSON.parse(e.dataTransfer.getData('application/json'));
        if (data.type === 'animationEffect') {
          // Play Model requires a GLB with animation clips on the target object.
          if (
            data.effect?.id === 'playModel' &&
            !canAddPlayModelToObject(objectId)
          ) {
            return;
          }
          // Use currentTimeIndicator.time for placement
          const indicatorTime = currentTimeIndicator?.time ?? 0;
          const startTime = Math.max(0, indicatorTime);
          // Always use default duration for new effects (do not reuse last-edited duration)
          const endTime = startTime + DEFAULT_NEW_EFFECT_DURATION;

          const currentObjectEffects = timelineEffects.filter(
            (effect) => effect.objectId === objectId
          );

          let availableRow = 0;
          const hasTimeOverlap = (start1, end1, start2, end2) => {
            return !(end1 <= start2 || start1 >= end2);
          };

          for (let row = 0; row < 20; row++) {
            const effectsInRow = currentObjectEffects.filter(
              (effect) => (effect.row || 0) === row
            );
            const hasOverlap = effectsInRow.some((effect) => {
              return hasTimeOverlap(
                startTime,
                endTime,
                effect.startTime,
                effect.endTime
              );
            });

            if (!hasOverlap) {
              availableRow = row;
              break;
            }

            if (
              row >=
              Math.max(...currentObjectEffects.map((e) => e.row || 0), -1)
            ) {
              availableRow = row + 1;
              break;
            }
          }

          const effectConfig = ANIMATION_PROPERTY_CONFIGS[data.effect.id];
          const configDefaults = effectConfig
            ? { ...effectConfig.properties }
            : {};

          const newEffect = {
            // Spread config defaults first so correct axes/properties are pre-set
            ...configDefaults,
            id: `${data.effect.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            effectType: data.effect.id,
            name: data.effect.name,
            icon: data.effect.icon,
            objectId: objectId,
            startTime: startTime,
            endTime: endTime,
            duration: DEFAULT_NEW_EFFECT_DURATION,
            row: availableRow,
            easing: animationSettings.easing,
            loop: animationSettings.loop,
            animationSpeed: animationSettings.animationSpeed !== undefined ? animationSettings.animationSpeed : (configDefaults.animationSpeed || 1),
            // Enhanced move effect properties
            movementType: animationSettings.movementType || 'linear',
            circleCenter: {
              ...(animationSettings.circleCenter || { x: 0, y: 0, z: 0 }),
            },
            circleRadius: animationSettings.circleRadius || 5,
            circleAxis: animationSettings.circleAxis || 'z',
            circleDirection: animationSettings.circleDirection || 'clockwise',
            circleStartAngle: animationSettings.circleStartAngle || 0,
            circleEndAngle: animationSettings.circleEndAngle || 360,
            circleRevolutions: animationSettings.circleRevolutions || 1,
            bezierControl1: {
              ...(animationSettings.bezierControl1 || { x: 5, y: 5, z: 0 }),
            },
            bezierControl2: {
              ...(animationSettings.bezierControl2 || { x: 10, y: -5, z: 0 }),
            },
            sineAmplitude: {
              ...(animationSettings.sineAmplitude || { x: 2, y: 0, z: 0 }),
            },
            sineFrequency: animationSettings.sineFrequency || 1,
            sinePhase: animationSettings.sinePhase || 0,
            sineAxis: animationSettings.sineAxis || 'x',
            figure8Radius: animationSettings.figure8Radius || 5,
            figure8Axis: animationSettings.figure8Axis || 'z',
            figure8Speed: animationSettings.figure8Speed || 0.5,
            spiralRadius: animationSettings.spiralRadius || 5,
            spiralHeight: animationSettings.spiralHeight || 5,
            spiralTurns: animationSettings.spiralTurns || 2,
            spiralAxis: animationSettings.spiralAxis || 'z',
            spiralDirection: animationSettings.spiralDirection || 'up',
            relativeToObject: animationSettings.relativeToObject || false,
            followPath: animationSettings.followPath || false,
            pathSmoothing: animationSettings.pathSmoothing || 0.5,
            autoRotate: animationSettings.autoRotate || false,
            lookAtTarget: {
              ...(animationSettings.lookAtTarget || { x: 0, y: 0, z: 0 }),
            },
            // Rotate effect properties
            startRotation: {
              ...(animationSettings.startRotation || { x: 0, y: 0, z: 0 }),
            },
            endRotation: {
              ...(animationSettings.endRotation || { x: 0, y: 0, z: 360 }),
            },
            rotationAxis: {
              ...(animationSettings.rotationAxis || {
                x: false,
                y: false,
                z: true,
              }),
            },
            rotationAmount: animationSettings.rotationAmount || 360,
            rotationSpeed: animationSettings.rotationSpeed || 1,
            rotationDirection:
              animationSettings.rotationDirection || 'clockwise',
            pivotPoint: {
              ...(animationSettings.pivotPoint || { x: 0, y: 0, z: 0 }),
            },
            // Fade effect properties
            // Fade effect properties
            startOpacity:
              animationSettings.startOpacity !== undefined
                ? animationSettings.startOpacity
                : data.effect.id === 'fadein'
                  ? 0
                  : 1,
            endOpacity:
              animationSettings.endOpacity !== undefined
                ? animationSettings.endOpacity
                : data.effect.id === 'fadein'
                  ? 1
                  : 0,
            fadeType:
              animationSettings.fadeType ||
              (data.effect.id === 'fadein' ? 'fadeIn' : 'fadeOut'),
            fadeSpeed: animationSettings.fadeSpeed || 1,
            // Bounce effect properties
            bounceHeight: animationSettings.bounceHeight || 5,
            bounceCount: animationSettings.bounceCount || 3,
            bounceDecay: animationSettings.bounceDecay || 0.8,
            bounceAxis: animationSettings.bounceAxis ?? 'y',
          };

          setTimelineEffects((prev) => {
            const withNewEffect = [...prev, newEffect];
            // Reorganize rows to fill gaps
            return reorganizeEffectRows(objectId, withNewEffect);
          });

          setSelectedObject(objectId);
          setSelectedEffects([newEffect.id]);

          if (
            endTime >
            calculateTimelineBounds(timelineEffects, effectEndTime).endTime
          ) {
            setEffectEndTime(endTime + 2);
            setMaxTimelineDuration(Math.max(maxTimelineDuration, endTime + 2));
          }
        }
      } catch (error) {
        console.error('Error handling timeline drop:', error);
      }
    },
    [
      timelineEffects,
      effectEndTime,
      maxTimelineDuration,
      animationSettings,
      currentTimeIndicator,
      setTimelineEffects,
      setSelectedObject,
      setSelectedEffects,
      setEffectEndTime,
      setMaxTimelineDuration,
      setIsDraggingOverTimeline,
      canAddPlayModelToObject,
    ]
  );

  const handleTimelineEffectDrop = useCallback(
    (e, targetObjectId) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        const data = JSON.parse(e.dataTransfer.getData('application/json'));
        if (data.type === 'timelineEffect' && draggedTimelineEffect) {
          // Don't move Play Model onto an object that has no GLB clips.
          if (
            draggedTimelineEffect.effectType === 'playModel' &&
            targetObjectId !== draggedTimelineEffect.objectId &&
            !canAddPlayModelToObject(targetObjectId)
          ) {
            return;
          }
          const rect = e.currentTarget.getBoundingClientRect();

          // FIXED: Properly calculate drop position
          const scrollLeft = timelineContainerRef?.current
            ? timelineContainerRef.current.scrollLeft
            : 0;

          // Get mouse X relative to the container
          const relativeX = e.clientX - rect.left;

          // Add scroll offset to get absolute position on timeline
          const absoluteX = relativeX + scrollLeft;

          // Subtract the drag offset (where user grabbed the effect)
          const adjustedX = dragOffset?.x
            ? absoluteX - dragOffset.x
            : absoluteX;

          // Convert pixels to time
          const markerIndex = adjustedX / PIXELS_PER_MARKER;
          const timePosition = markerIndex * timelineScale;

          const effectDuration =
            draggedTimelineEffect.endTime - draggedTimelineEffect.startTime;
          const newStartTime = Math.max(0, timePosition);
          const newEndTime = newStartTime + effectDuration;

          const targetObjectEffects = timelineEffects.filter(
            (effect) =>
              effect.objectId === targetObjectId &&
              effect.id !== draggedTimelineEffect.id
          );

          let availableRow = 0;
          const hasTimeOverlap = (start1, end1, start2, end2) => {
            return !(end1 <= start2 || start1 >= end2);
          };

          for (let row = 0; row < 20; row++) {
            const effectsInRow = targetObjectEffects.filter(
              (effect) => (effect.row || 0) === row
            );
            const hasOverlap = effectsInRow.some((effect) => {
              return hasTimeOverlap(
                newStartTime,
                newEndTime,
                effect.startTime,
                effect.endTime
              );
            });

            if (!hasOverlap) {
              availableRow = row;
              break;
            }

            if (
              row >= Math.max(...targetObjectEffects.map((e) => e.row || 0), -1)
            ) {
              availableRow = row + 1;
              break;
            }
          }

          setTimelineEffects((prev) => {
            // First update the dragged effect
            const updated = prev.map((effect) =>
              effect.id === draggedTimelineEffect.id
                ? {
                  ...effect,
                  startTime: newStartTime,
                  endTime: newEndTime,
                  objectId: targetObjectId,
                  row: availableRow,
                }
                : effect
            );
            // Then reorganize rows to fill gaps
            return reorganizeEffectRows(targetObjectId, updated);
          });

          setSelectedObject(targetObjectId);
          setSelectedEffects([draggedTimelineEffect.id]);

          if (
            newEndTime >
            calculateTimelineBounds(timelineEffects, effectEndTime).endTime
          ) {
            setEffectEndTime(newEndTime + 2);
            setMaxTimelineDuration(
              Math.max(maxTimelineDuration, newEndTime + 2)
            );
          }
        }
      } catch (error) {
        console.error('Error handling timeline effect drop:', error);
      }
    },
    [
      draggedTimelineEffect,
      dragOffset,
      effectStartTime,
      effectEndTime,
      timelineEffects,
      maxTimelineDuration,
      timelineScale,
      timelineContainerRef,
      setTimelineEffects,
      setSelectedObject,
      setSelectedEffects,
      setEffectEndTime,
      setMaxTimelineDuration,
      canAddPlayModelToObject,
    ]
  );

  // Drag and drop handlers
  const handleEffectDragStart = useCallback(
    (e, effect) => {
      setDraggedEffect(effect);
      e.dataTransfer.setData(
        'application/json',
        JSON.stringify({
          type: 'animationEffect',
          effect: effect,
        })
      );
      e.dataTransfer.effectAllowed = 'copy';
    },
    [setDraggedEffect]
  );

  const handleEffectDragEnd = useCallback(() => {
    setDraggedEffect(null);
    setIsDraggingOverTimeline(false);
  }, [setDraggedEffect, setIsDraggingOverTimeline]);

  const handleTimelineEffectDragStart = useCallback(
    (e, effect) => {
      e.stopPropagation();
      setDraggedTimelineEffect(effect);

      // FIXED: Calculate offset correctly
      const rect = e.currentTarget.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      setDragOffset({ x: offsetX, y: 0 });

      e.dataTransfer.setData(
        'application/json',
        JSON.stringify({
          type: 'timelineEffect',
          effectId: effect.id,
          effect: effect,
        })
      );
      e.dataTransfer.effectAllowed = 'move';
    },
    [setDraggedTimelineEffect, setDragOffset]
  );

  const handleTimelineEffectDragEnd = useCallback(
    (e) => {
      e.stopPropagation();
      setDraggedTimelineEffect(null);
      setDragOffset({ x: 0, y: 0 });
      setIsDraggingOverTimeline(false);
    },
    [setDraggedTimelineEffect, setDragOffset, setIsDraggingOverTimeline]
  );

  const handleTimelineDragLeave = useCallback(() => {
    setIsDraggingOverTimeline(false);
  }, [setIsDraggingOverTimeline]);

  // Time marker click handler
  const handleTimeMarkerClick = useCallback(
    (e) => {
      if (isPlaying) {
        setIsPlaying(false);
        setPlaybackStartTime(null);
      }

      const rect = e.currentTarget.getBoundingClientRect();
      const { timePosition, positionPercent } = calculateTimelinePosition(
        e.clientX,
        rect,
        effectStartTime,
        effectEndTime
      );
      setCurrentTimeIndicator({
        position: positionPercent,
        time: timePosition,
        visible: true,
      });
    },
    [
      isPlaying,
      effectStartTime,
      effectEndTime,
      setIsPlaying,
      setPlaybackStartTime,
      setCurrentTimeIndicator,
    ]
  );

  // FIXED: Effect resize handler with automatic row reorganization
  const handleEffectResize = useCallback(
    (effectId, newEndTime, origin = null) => {
      const validEndTime = Math.max(0.001, snapTimeToMilli(newEndTime));
      let resized = null;

      setTimelineEffects((prev) => {
        const current = prev.find((e) => e.id === effectId);
        if (!current) return prev;

        resized = applyDurationResizeToEffect(current, validEndTime, origin);
        const updated = prev.map((e) => (e.id === effectId ? resized : e));
        return reorganizeEffectRows(current.objectId, updated);
      });

      if (resized) {
        const speedKey = getEffectSpeedKey(resized);
        setAnimationSettings((settings) => ({
          ...settings,
          duration: resized.duration,
          animationSpeed:
            resized.animationSpeed ?? settings.animationSpeed ?? 1,
          playSpeed: resized.playSpeed ?? settings.playSpeed ?? 1,
          [speedKey]: resized[speedKey],
        }));
      }

      if (
        validEndTime >
        calculateTimelineBounds(timelineEffects, effectEndTime).endTime
      ) {
        setEffectEndTime(validEndTime + 2);
        setMaxTimelineDuration(Math.max(maxTimelineDuration, validEndTime + 2));
      }
    },
    [
      timelineEffects,
      effectEndTime,
      maxTimelineDuration,
      setTimelineEffects,
      setEffectEndTime,
      setMaxTimelineDuration,
      setAnimationSettings,
    ]
  );

  // Remove timeline effect
  const removeTimelineEffect = useCallback(
    (effectId) => {
      // Find the effect to get its objectId
      const effectToRemove = timelineEffects.find(
        (effect) => effect.id === effectId
      );
      if (!effectToRemove) return;

      // Update local state and reorganize rows
      setTimelineEffects((prev) => {
        const filtered = prev.filter((effect) => effect.id !== effectId);
        // Reorganize rows after removal to fill gaps
        return reorganizeEffectRows(effectToRemove.objectId, filtered);
      });

      setSelectedEffects((prev) => prev.filter((id) => id !== effectId));
    },
    [setTimelineEffects, setSelectedEffects, timelineEffects]
  );

  return {
    handleTimelineDrop,
    handleTimelineEffectDrop,
    handleEffectDragStart,
    handleEffectDragEnd,
    handleTimelineEffectDragStart,
    handleTimelineEffectDragEnd,
    handleTimelineDragLeave,
    handleTimeMarkerClick,
    handleEffectResize,
    removeTimelineEffect,
    canAddPlayModelToObject,
  };
};
